import { FC, useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import { useMessagingContext } from '../../../contexts/messaging.context';
import {
  useGetGroupMessagesHook,
  useGetGroupsHook,
  useSendGroupMessageHook,
  getGroupMessagesKey,
} from '../../../hooks/useMessagingHook';
import { useQueryClient } from '@tanstack/react-query';
import { Message } from '../../../../../../apis/messaging/messaging.interfaces';
import { localStorageGet } from '../../../../../../utils/localStorage';
import { ILoggedInUser } from '../../../../../../apis/auth/auth.interfaces';

function formatTime(dateStr: string | Date): string {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateSeparator(dateStr: string | Date): string {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString();
}

const GroupChatWindow: FC = () => {
  const { state } = useMessagingContext();
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const groupId = state.selectedGroupId;

  const { data: groupsData } = useGetGroupsHook({ page: 1, limit: 50 });
  const selectedGroup = groupsData?.groups?.find((g) => g.group._id === groupId);

  const {
    data: messagesData,
    isLoading,
  } = useGetGroupMessagesHook(groupId, { page: 1, limit: 50 });

  const messages = messagesData?.data || [];

  const sendGroupMessage = useSendGroupMessageHook({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getGroupMessagesKey, groupId] });
    },
  });

  const userFromStorage = localStorageGet('loggedInUser', undefined);
  const currentUser: ILoggedInUser | null =
    userFromStorage && typeof userFromStorage === 'object'
      ? (userFromStorage as ILoggedInUser)
      : null;
  const currentUserId = currentUser?.id || '';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = useCallback(() => {
    if (!messageText.trim() || !groupId) return;
    sendGroupMessage.mutate({
      group_id: groupId,
      content: messageText.trim(),
    });
    setMessageText('');
  }, [messageText, groupId, sendGroupMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!groupId) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <GroupIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
        <Typography color="text.secondary">Select a group to start chatting</Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ml: { xs: 0, md: 1 },
        borderRadius: { xs: 0, sm: 1 },
      }}
      data-testid="group-chat-window"
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
          <GroupIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {selectedGroup?.group.name || 'Group'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedGroup?.memberCount || 0} members
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" variant="body2">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        ) : (
          <>
            {[...messages].reverse().map((msg: Message, index: number, arr: Message[]) => {
              const msgId = msg._id || msg.id || String(index);
              const isMine = msg.sender_id === currentUserId;
              const showDateSeparator =
                index === 0 ||
                new Date(msg.created_at).toDateString() !==
                new Date(arr[index - 1].created_at).toDateString();

              return (
                <Box key={msgId}>
                  {showDateSeparator && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'center', my: 1.5 }}
                    >
                      {formatDateSeparator(msg.created_at)}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: isMine ? 'flex-end' : 'flex-start',
                      mb: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        bgcolor: isMine ? 'primary.main' : 'grey.100',
                        color: isMine ? 'white' : 'text.primary',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.75,
                      }}
                    >
                      {!isMine && (
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color={isMine ? 'inherit' : 'primary.main'}
                          sx={{ display: 'block', mb: 0.25 }}
                        >
                          {msg.sender?.first_name
                            ? `${msg.sender.first_name} ${msg.sender.last_name}`
                            : 'Member'}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'right',
                          opacity: 0.7,
                          mt: 0.25,
                          fontSize: '0.65rem',
                        }}
                      >
                        {formatTime(msg.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sendGroupMessage.isPending}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSend}
                  disabled={!messageText.trim() || sendGroupMessage.isPending}
                  color="primary"
                  size="small"
                >
                  {sendGroupMessage.isPending ? (
                    <CircularProgress size={18} />
                  ) : (
                    <SendIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Paper>
  );
};

export default GroupChatWindow;
